/**
 * Camera helpers shared by every place this app puts a face on the screen.
 *
 * TWO CALL SITES, ONE COPY. These four functions were written for the 1:1
 * empathy call (`components/empathy/PeerAudioRoom.tsx` and
 * `utils/PeerConnection.ts` on `feat/1to1-video`) and are lifted here verbatim
 * — same behaviour, same strings — because circle video needs exactly the same
 * four answers. Two copies of `cameraErrorMessage` is how a user gets told
 * "Camera blocked" on one screen and "Permission denied" on the other for the
 * same `NotAllowedError`; two copies of the encoder cap is how one path quietly
 * ships 200 kbps into a room budgeted for 90 (class D).
 *
 * When `feat/1to1-video` merges, its private copies come out and it imports
 * from here. Nothing in this file knows about circles or about 1:1.
 */

/**
 * ─── THE ENCODE TABLE ──────────────────────────────────────────────────────
 *
 * ONE TABLE, FOUR ROWS, TWO SCREENS. What a camera captures, what the encoder
 * is allowed to spend, how big the picture is on the wire and which codec is
 * asked for first are ONE decision per feed — and they were four constants read
 * in two files. `PeerConnection.ts` held its own `VIDEO_MAX_BITRATE_BPS =
 * 200_000` and no capture size at all, while `useCircleVideo` read
 * SPEAKER_MAX_BPS/SPEAKER_TARGET_PX from here; raising one of a row's numbers
 * without the others is how a path ends up spending a bigger budget on pixels
 * nobody renders, or asking for 640px and shipping it at 90 kbps (class C — the
 * set measured is the set acted on).
 *
 * THE CIRCLE'S LARGE ROW MOVED ON 2026-10-05; ITS SMALL ROW DID NOT. An SFU
 * forwards each feed to everyone else in the room, so the circle's ceiling is a
 * SERVER budget as well as a client one (docs/plans/CIRCLE-VIDEO.md §5) and the
 * room is created with a matching `bitrate` + `bitrate_cap`
 * (`JanusService::VIDEO_ROOM_BITRATE_BPS`). David picked the large feed by eye
 * from a phone preview of his own clip: 360×640 portrait, 15 fps, 400 kbps.
 * The small feed stays 128px / 40 kbps. Raising one half without the other buys
 * nothing — the server cap is the hard bound — so both moved together.
 *
 * THE 1:1'S TWO ROWS ARE NEW. A 1:1 is peer-to-peer: nothing is forwarded,
 * nothing is transcoded, and the only budget is the two people's own links. It
 * was nonetheless shipping the circle's tiny 240×240 capture behind a 200 kbps
 * cap, drawn across the whole of a phone screen. David, 2026-09-14: 640×640 at
 * up to 24 fps, 900 kbps, VP9 first, `degradationPreference: 'balanced'` — and
 * a cellular link, or anybody who asks for it, gets 480×480 at 400 kbps
 * instead.
 */

export interface VideoProfile {
  /** What to ask `getUserMedia` for. */
  capture: MediaTrackConstraints;
  /** Payload ceiling in bits per second, applied with `setParameters`. */
  maxBitrate: number;
  maxFramerate: number;
  /**
   * The longest edge this feed is ever ENCODED at. The capture constraints ask
   * for it, but `ideal` is a WISH: a webcam with no 128×128 mode hands back
   * 640×480 and the encoder then spends the whole budget on pixels nobody
   * renders. `capVideoEncoding` scales the sender down to this.
   */
  targetPx: number;
  /**
   * Ask for VP9 ahead of everything else. Best-effort everywhere — see
   * `preferVp9` for why every other codec stays BEHIND it rather than being
   * removed.
   */
  preferVp9: boolean;
  /**
   * What the encoder should give up first when the link cannot carry the
   * budget. `'balanced'` on a face-to-face call: `'maintain-resolution'` turns a
   * conversation into a slideshow and `'maintain-framerate'` turns a face into
   * a smear, and neither is the right answer for a 1:1 where the whole point is
   * the other person's expression. Undefined leaves the browser's own default,
   * which is what the circle has always run.
   */
  degradationPreference?: RTCDegradationPreference;
}

/**
 * THE CIRCLE'S CAPTURE, singular — one for both sizes.
 *
 * 360×640 PORTRAIT at 15 fps (David, 2026-10-05): the large feed is drawn
 * full screen in a portrait rectangle (`pinnedStageRect`, 328×467 at 360×800)
 * with `object-fit: cover`, so a portrait source fills it with only a thin
 * band cropped top and bottom, and the small circle tile crops a centred
 * square out of the middle of the same frame — the face. It used to be a
 * 240×240 square, which was right while the biggest face was a 200px circle.
 *
 * WHY ONE CAPTURE AND NOT TWO. The obvious shape is a large capture for the
 * floor holder and a small one for everyone else. It does not survive the
 * floor: the picture follows the voice, several times a turn, and every turn
 * rotation promotes the outgoing listener to speaker — so re-capturing per
 * size would blink the camera at every flip. What actually costs is the
 * ENCODED resolution, and that is `scaleResolutionDownBy` on the sender,
 * applied by `capVideoEncoding` below and changed by `setParameters` with no
 * renegotiation and no blink. The small feed is 72×128 at 40 kbps on the wire
 * (the longest edge, 128, is the row's `targetPx`); the large is 360×640 at
 * 400. A flip costs the encoder about half a second of softness while it
 * ramps, and nothing else.
 *
 * `ideal`, not `exact`: a camera with no 360×640 mode answers with its nearest
 * mode instead of failing the capture, and `capVideoEncoding` scales whatever
 * arrived to the row's longest edge.
 *
 * This is the capture as WANTED. What is ASKED of `getUserMedia` is
 * `circleCaptureConstraints()` below — the same capture in the camera's own
 * terms, which on a phone held in portrait swaps the two numbers.
 */
export const VIDEO_CAPTURE_CONSTRAINTS: MediaTrackConstraints = {
  width: { ideal: 360 },
  height: { ideal: 640 },
  frameRate: { ideal: 15, max: 15 },
  facingMode: 'user',
};

/**
 * THE SAME CAPTURE, ASKED FOR IN THE CAMERA'S TERMS.
 *
 * A phone's sensor is landscape, and the browser evaluates `width`/`height`
 * against it BEFORE it rotates the frames into the screen's orientation.
 * Measured on David's phone (Capacitor WebView, held in portrait,
 * 2026-10-05): asking 360×640 delivered a LANDSCAPE 640×360 (a crop-and-scale
 * of the sensor frame), and asking 640×360 delivered the 360×640 portrait we
 * want, from a native mode. A desktop webcam is not rotated, so there the
 * portrait is asked for as it is wanted (crop-and-scale; 360×640 measured on
 * the wire in `e2e/audio/circle-video-floor.spec.js`).
 *
 * "Held in portrait" is a touch device whose screen reports a portrait
 * orientation. A browser that reports no orientation gets the canonical
 * constraint. Whatever arrives, `capVideoEncoding` sizes the send by the
 * longest edge, so a wrong guess costs the crop, never the budget.
 */
export function circleCaptureConstraints(
  env: { orientation: string | null; maxTouchPoints: number } = readScreenEnv(),
): MediaTrackConstraints {
  const portraitTouch = env.maxTouchPoints > 0 && !!env.orientation?.startsWith('portrait');
  if (!portraitTouch) return VIDEO_CAPTURE_CONSTRAINTS;
  return { ...VIDEO_CAPTURE_CONSTRAINTS, width: { ideal: 640 }, height: { ideal: 360 } };
}

function readScreenEnv(): { orientation: string | null; maxTouchPoints: number } {
  try {
    return {
      orientation:
        typeof screen !== 'undefined' && screen.orientation?.type ? screen.orientation.type : null,
      maxTouchPoints: typeof navigator !== 'undefined' ? (navigator.maxTouchPoints ?? 0) : 0,
    };
  } catch {
    return { orientation: null, maxTouchPoints: 0 };
  }
}

/** Matches the `max` in the circle's constraints — one number, not two places. */
export const VIDEO_MAX_FRAMERATE = 15;

/**
 * THE 1:1's CAPTURE at full quality — 640×640 at up to 24 fps.
 *
 * Square, like the circle's, because the stage crops to a rounded rectangle and
 * a 16:9 capture would be thrown away down both sides. 24 rather than 30: the
 * encoder spends its budget on motion it is given, and a contemplative
 * conversation has almost none — 24 buys the same smoothness at four-fifths of
 * the frames.
 */
export const CALL_CAPTURE_CONSTRAINTS: MediaTrackConstraints = {
  width: { ideal: 640 },
  height: { ideal: 640 },
  frameRate: { ideal: 24, max: 24 },
  facingMode: 'user',
};

/** The same capture, asked for smaller, for a metered link. */
export const CALL_SAVER_CAPTURE_CONSTRAINTS: MediaTrackConstraints = {
  width: { ideal: 480 },
  height: { ideal: 480 },
  frameRate: { ideal: 24, max: 24 },
  facingMode: 'user',
};

export type VideoProfileName =
  | 'circle-speaker'
  | 'circle-listener'
  | 'circle-gallery'
  | 'call-full'
  | 'call-saver';

export const VIDEO_PROFILES: Readonly<Record<VideoProfileName, VideoProfile>> = {
  /**
   * THE CIRCLE'S LARGE FEED — whoever is drawn big: the floor holder in an
   * exchange (the speaker by default, the listener while they have the floor),
   * and every camera that sends in the discussion spotlight. 360×640 portrait,
   * 15 fps, 400 kbps, chosen by eye by David on 2026-10-05.
   *
   * NEXT STEP IF WE NEED TO SAVE DATA: 13 fps / 346 kbps also looked fine in
   * the same preview. Change `maxFramerate`, the capture's frame rate and this
   * bitrate together.
   *
   * The room is created with a matching hard cap
   * (`JanusService::VIDEO_ROOM_BITRATE_BPS`, `bitrate_cap: true`), so raising
   * the client half alone buys nothing; a room created before that cap moved
   * holds this feed to its old ceiling until the circle ends.
   */
  'circle-speaker': {
    capture: VIDEO_CAPTURE_CONSTRAINTS,
    maxBitrate: 400_000,
    maxFramerate: VIDEO_MAX_FRAMERATE,
    targetPx: 640,
    preferVp9: true,
  },
  /**
   * THE CIRCLE'S SMALL FEED — the face in the floating circle: 128px longest
   * edge (72×128 from the portrait capture) at 40 kbps. Unchanged on
   * 2026-10-05.
   */
  'circle-listener': {
    capture: VIDEO_CAPTURE_CONSTRAINTS,
    maxBitrate: 40_000,
    maxFramerate: VIDEO_MAX_FRAMERATE,
    targetPx: 128,
    preferVp9: true,
  },
  /**
   * THE CIRCLE'S GALLERY FEED — a camera-on participant who is not drawn big
   * or small on page 1, seen only in a page-2 tile
   * (docs/plans/CIRCLE-VIDEO-GALLERY.md §2.1): the same 128 px as the small
   * row (72×128, scale 5 from the 360×640 capture) at 8 fps and 30 kbps. The
   * small row keeps 15 fps / 40 kbps because it is the face opposite the
   * speaker, where expression matters more than on a grid tile.
   *
   * INERT until `CIRCLE_GRID_VIDEO` is 'gallery': only `senderRow` under that
   * switch answers `size: 'gallery'`. It is here, in the gallery's PR 1, so the
   * budget's size → row lookup is total rather than defaulted.
   */
  'circle-gallery': {
    capture: VIDEO_CAPTURE_CONSTRAINTS,
    maxBitrate: 30_000,
    maxFramerate: 8,
    targetPx: 128,
    preferVp9: true,
  },
  /**
   * The 1:1 on an unmetered link. P2P: no SFU, no fan-out, no server egress at
   * all, and the picture fills a phone screen rather than a 150px circle. 900
   * kbps is roughly what a 640p VP9 face needs to stop looking like a
   * compression artefact, and it is one link's worth — not eleven.
   */
  'call-full': {
    capture: CALL_CAPTURE_CONSTRAINTS,
    maxBitrate: 900_000,
    maxFramerate: 24,
    targetPx: 640,
    preferVp9: true,
    degradationPreference: 'balanced',
  },
  /**
   * The 1:1 on cellular, or with "Data saver" on. The two levers David named
   * and no others: 480×480 and 400 kbps. The frame rate is deliberately NOT
   * dropped as well — a metered link is a reason to send fewer pixels, and a
   * face at 12 fps reads as a fault rather than as a saving.
   */
  'call-saver': {
    capture: CALL_SAVER_CAPTURE_CONSTRAINTS,
    maxBitrate: 400_000,
    maxFramerate: 24,
    targetPx: 480,
    preferVp9: true,
    degradationPreference: 'balanced',
  },
} as const;

/** The circle's two rows, by the names the code that reads them already uses. */
export const SPEAKER_TARGET_PX = VIDEO_PROFILES['circle-speaker'].targetPx;
export const LISTENER_TARGET_PX = VIDEO_PROFILES['circle-listener'].targetPx;
export const SPEAKER_MAX_BPS = VIDEO_PROFILES['circle-speaker'].maxBitrate;
export const LISTENER_MAX_BPS = VIDEO_PROFILES['circle-listener'].maxBitrate;

/**
 * WHAT THE BROWSER WILL TELL US ABOUT THE LINK, AND HOW MUCH OF IT TO BELIEVE.
 *
 * `navigator.connection` is the Network Information API. It is Chromium-only —
 * absent in Safari and in Firefox — and the three fields answer three different
 * questions, so they are read in the order of how directly each answers THIS
 * one ("is this link metered?"):
 *
 *   saveData       the person has turned the browser's own data saver on. That
 *                  is not an inference about the link, it is an instruction
 *                  about data, and it is the most direct answer there is.
 *   type           the TRANSPORT — `'cellular'`, `'wifi'`, `'ethernet'`,
 *                  `'none'`, `'unknown'`. When it is present and is not
 *                  `'unknown'`, it answers the question exactly. Chrome on
 *                  Android reports it; desktop Chrome usually does not.
 *   effectiveType  a SPEED estimate, `'slow-2g' | '2g' | '3g' | '4g'`, derived
 *                  from observed round-trip times. Never says "wifi" — the
 *                  spec has no such value — so the rule is "anything short of
 *                  4g". It is the weakest signal and deliberately last: a slow
 *                  hotel wifi reads `'3g'`, and dropping to the saver profile
 *                  there is the RIGHT call even though nothing is metered.
 *
 * ABSENT MEANS FULL. Safari and Firefox answer nothing at all, and the
 * alternative — assuming cellular — would hand every desktop Firefox user a
 * 480px call for a link that is almost certainly fibre. The person who really
 * is on a metered link and whose browser cannot say so has the "Data saver"
 * switch, which is the whole reason that switch exists rather than being left
 * to detection.
 */
interface NetworkInformationLike {
  type?: string;
  effectiveType?: string;
  saveData?: boolean;
}

export function readConnection(): NetworkInformationLike | null {
  if (typeof navigator === 'undefined') return null;
  const nav = navigator as Navigator & { connection?: NetworkInformationLike };
  return nav.connection ?? null;
}

export function isMeteredConnection(
  connection: NetworkInformationLike | null | undefined = readConnection(),
): boolean {
  if (!connection) return false;
  if (connection.saveData === true) return true;
  const type = connection.type;
  if (type && type !== 'unknown') return type === 'cellular';
  const effective = connection.effectiveType;
  if (effective) return effective !== '4g';
  return false;
}

/**
 * Which of the 1:1's two rows applies right now.
 *
 * `dataSaver` — the person's own `video_data_saver` preference — WINS
 * outright and is checked first: it is an explicit instruction, and a
 * preference that a wifi detection could override would be a preference that
 * does nothing on the only link most people use it on.
 */
export function selectCallVideoProfile({
  dataSaver,
  connection,
}: {
  dataSaver: boolean;
  connection?: NetworkInformationLike | null;
}): VideoProfile {
  if (dataSaver) return VIDEO_PROFILES['call-saver'];
  return isMeteredConnection(connection === undefined ? readConnection() : connection)
    ? VIDEO_PROFILES['call-saver']
    : VIDEO_PROFILES['call-full'];
}

/**
 * THE DEVICE WENT AWAY UNDER US — the message for a camera that was working and
 * stopped, as opposed to one that never opened.
 *
 * It lives here, beside `cameraFailure`, because BOTH screens say it: circle
 * video (`useCircleVideo`, where it is re-exported as `VIDEO_STOPPED_MSG`
 * beside its four room-side siblings) and the 1:1 call. Those two used to say
 * "Your camera stopped. Tap to try again." and "Camera stopped. Audio is
 * unaffected." for the same event — the exact drift this module was created to
 * end (class D).
 *
 * It names the camera and then an action, like every branch of `cameraFailure`,
 * and it is TRANSIENT wherever a caller tracks that: a camera stolen by another
 * app, a thermal throttle, a lock screen are all things that stop being true on
 * their own. The device-side failures that really do outlive the moment are the
 * `persistent` branches above.
 */
export const CAMERA_STOPPED_MSG = 'Your camera stopped. Tap to try again.';

/**
 * There is no camera at all. The same sentence `cameraFailure` gives for
 * `NotFoundError`, named once so the "we got a stream with no video track in
 * it" caller cannot drift from it.
 */
export const CAMERA_NONE_MSG = 'No camera found on this device.';

/**
 * What the person is told when the camera did not open, and how long it stays
 * true for.
 *
 * Not the raw DOMException: "NotReadableError: Could not start video source" is
 * a sentence for a log. Every branch names the CAMERA and then an action, and
 * fits on one line of a 240px status chip — a two-line chip at 360px covers the
 * controls it is sitting above (measured in the 360×800 dev preview). Naming
 * the camera first is also what carries "the call is fine": a sentence about
 * your camera cannot be read as a sentence about the conversation.
 *
 * ONE SWITCH, TWO ANSWERS. `persistent` asks WHO CAN FIX IT, not how bad it is.
 * Every device-side failure here needs the person to go and do something —
 * allow the camera in Settings, quit the app that is holding it, use a device
 * that has one — so the message has to still be on screen when they come back
 * from doing it. A message that vanished while they were in Settings is a
 * message they never got. The generic fallback is a system that may already
 * have recovered, so it expires like the room-side refusals do.
 *
 * Two functions over one switch rather than two switches, so "what do we say"
 * and "how long do we say it for" cannot drift apart (class C).
 */
export function cameraFailure(err: unknown): {
  message: string;
  persistent: boolean;
  endsIntent?: true;
} {
  const name = (err as { name?: string } | null)?.name;
  // `endsIntent`: the two failures that asking again cannot fix — the OS said
  // no, or there is no camera. A caller that keeps a standing camera-on intent
  // (the circle: it comes back on by itself at the person's next turn) drops it
  // for these, so a revoked permission fails quietly to off once instead of
  // being asked about again at every turn. Every other failure here may have
  // cleared by the next turn (the other app let go of the camera).
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return {
      message: 'Camera blocked. Allow it in settings.',
      persistent: true,
      endsIntent: true,
    };
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError') {
    return { message: CAMERA_NONE_MSG, persistent: true, endsIntent: true };
  }
  if (name === 'NotReadableError') {
    return {
      message: 'Close the other app using your camera.',
      persistent: true,
    };
  }
  return { message: 'Camera unavailable. Tap to try again.', persistent: false };
}

/** The words alone, for callers that have nowhere to put the second half. */
export function cameraErrorMessage(err: unknown): string {
  return cameraFailure(err).message;
}

/** srcObject is a property, not an attribute — JSX cannot set it. */
export function attachStream(el: HTMLVideoElement | null, stream: MediaStream | null): void {
  if (!el) return;
  try {
    el.srcObject = stream;
  } catch {
    return; // non-media environments (jsdom)
  }
  if (!stream) return;
  // Optional all the way down: jsdom has no play(), and autoplay rejection on
  // a stream swap is not something the call should care about.
  void el.play?.()?.catch(() => {});
}

/**
 * Ask for VP9 first, keeping every other codec BEHIND it rather than instead of
 * it, so a browser without VP9 still negotiates and the documented server-side
 * escape hatch still exists.
 *
 * That last clause is the reason this is not janus.js's own `track.codec`
 * option: janus.js filters capabilities down to the one mime type and calls
 * `setCodecPreferences` with only those, which strips VP8 out of the offer
 * entirely. `docs/plans/CIRCLE-VIDEO.md` §8.5 says the fix for VP9 being
 * expensive on a mid-range Android is to flip the ROOM to `'vp8,vp9'` with no
 * client change — and that flip can only work if the client offered both.
 *
 * Best-effort: unsupported in Firefox and in older browsers, and never worth
 * failing a call over. Returns whether the preference was actually applied.
 */
export function preferVp9(transceiver: RTCRtpTransceiver | null | undefined): boolean {
  try {
    if (!transceiver || typeof transceiver.setCodecPreferences !== 'function') return false;
    const capabilities =
      typeof RTCRtpSender !== 'undefined' && RTCRtpSender.getCapabilities
        ? RTCRtpSender.getCapabilities('video')
        : null;
    const codecs = capabilities?.codecs;
    if (!codecs?.length) return false;
    const isVp9 = (c: RTCRtpCodec) => /vp9/i.test(c.mimeType);
    const preferred = codecs.filter(isVp9);
    if (!preferred.length) return false;
    transceiver.setCodecPreferences([...preferred, ...codecs.filter((c) => !isVp9(c))]);
    return true;
  } catch (err) {
    console.warn('VP9 codec preference not applied:', err);
    return false;
  }
}

export interface VideoEncodingCap {
  /** Payload ceiling in bits per second. */
  maxBitrate: number;
  maxFramerate?: number;
  /**
   * The longest edge this feed is ever rendered at. The capture constraints ask
   * for it, but `ideal` is a WISH: a webcam with no 128×128 mode hands back
   * 640×480 and the encoder then spends the whole budget on pixels nobody
   * renders — a listener tile at 5 fps and 40 kbps of blur. When the track
   * really is bigger than the tile, this asks the encoder to scale it down.
   * Omit to leave `scaleResolutionDownBy` alone.
   */
  targetPx?: number;
  /**
   * What to give up first when the link cannot carry the budget. Omit to leave
   * the browser's own default, which is what every caller did before the 1:1
   * asked for `'balanced'`.
   */
  degradationPreference?: RTCDegradationPreference;
}

/**
 * Hold a sender to its budget. `setParameters` works at any point after the
 * sender exists, so unlike the codec preference this one applies to the
 * negotiation already in flight.
 *
 * Returns whether the cap was applied — class B: the caller can tell a cap it
 * got from a cap the browser refused, instead of assuming success from a void.
 */
export async function capVideoEncoding(
  sender: RTCRtpSender | null | undefined,
  cap: VideoEncodingCap,
  /**
   * The capture to size `targetPx` against, when the sender may not be holding
   * it — a circle spotlight pause detaches the track (`replaceTrack(null)`)
   * while the capture keeps running. Defaults to the sender's own track.
   */
  sizeTrack?: MediaStreamTrack | null,
): Promise<boolean> {
  try {
    if (!sender) return false;
    const params = sender.getParameters?.();
    if (!params) return false;
    if (!params.encodings?.length) params.encodings = [{}];
    params.encodings[0].maxBitrate = cap.maxBitrate;
    params.encodings[0].maxFramerate = cap.maxFramerate ?? VIDEO_MAX_FRAMERATE;
    // A SEND-PARAMETERS field, not a per-encoding one: it describes what the
    // encoder as a whole should sacrifice, so writing it onto `encodings[0]`
    // (the obvious place, beside the two above) sets nothing at all.
    if (cap.degradationPreference) params.degradationPreference = cap.degradationPreference;

    if (cap.targetPx && cap.targetPx > 0) {
      const settings = (sizeTrack ?? sender.track)?.getSettings?.();
      const longest = Math.max(settings?.width ?? 0, settings?.height ?? 0);
      // Never scale UP (a factor below 1 is invalid) and never bother the
      // encoder with 1.02× — a capture that already fits stays as it is.
      const factor = longest > 0 ? longest / cap.targetPx : 1;
      params.encodings[0].scaleResolutionDownBy = factor > 1.05 ? factor : 1;
    }

    await sender.setParameters(params);
    return true;
  } catch (err) {
    console.warn('Video encoding cap not applied:', err);
    return false;
  }
}

/**
 * THE WHOLE ROW, APPLIED — the codec preference and the budget together, from
 * one profile, so a caller cannot take half of it.
 *
 * Both halves are best-effort and both report whether they landed, separately,
 * because the codec half is EXPECTED to be false on Firefox: reporting them as
 * one boolean is how a routine codec-preference miss reads as a budget failure.
 */
export async function applyVideoProfile(
  transceiver: RTCRtpTransceiver | null | undefined,
  profile: VideoProfile,
  /** See `capVideoEncoding`. */
  sizeTrack?: MediaStreamTrack | null,
): Promise<{ codec: boolean; cap: boolean }> {
  const codec = profile.preferVp9 ? preferVp9(transceiver) : false;
  const cap = await capVideoEncoding(
    transceiver?.sender,
    {
      maxBitrate: profile.maxBitrate,
      maxFramerate: profile.maxFramerate,
      targetPx: profile.targetPx,
      degradationPreference: profile.degradationPreference,
    },
    sizeTrack,
  );
  return { codec, cap };
}
